import { memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import "./NavLogProductCard.scss";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import ProductDestinationTitleContainer from "app/pages/.shared/ProductDestinationTitle/ProductDestinationTitleContainer";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import { resolutionPropType } from "app/utils/propTypes";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import { FormattedMessage } from "react-intl";
import { sendTagOnNavlogClick, sendTagOnNavlogProductClick } from "app/utils/analytics";
import { useNavigate } from "react-router-dom";
import ProductTopBar from "app/pages/Listing/Product/ProductTopBar/ProductTopBar";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const IMG_SIZES_DESKTOP = {
	small: { width: 768, height: 416 },
	medium: { width: 768, height: 416 },
	large: { width: 502, height: 282 },
	xlarge: { width: 502, height: 282 },
};

const CLOUDINARY_OPTIONS = ["c_fill"];

const NavLogProductCard = ({
	uri,
	resolution,
	photo = {},
	name,
	expiration = {},
	region,
	resort,
	country,
	category,
	categoryType,
	quotationURL,
}) => {
	const navigate = useNavigate();

	const handleProductClick = () => {
		if (quotationURL) {
			window.location = quotationURL; // quotationURL is an absolute url
		} else {
			navigate(`/${uri}`);
		}

		sendTagOnNavlogClick({ quotationURL, productUri: uri });
		sendTagOnNavlogProductClick({
			uri,
			resolution,
			photo,
			name,
			expiration,
			region,
			resort,
			country,
			category,
			categoryType,
			quotationURL,
			lastSeenProductType: quotationURL ? "quote" : "product",
		});
	};

	const isExpired = expiration.day === 0 && expiration.hour === 0;
	const productClassName = classNames({
		"navlog-product-card": true,
		"navlog-product-card--expired": isExpired,
	});

	return (
		<div className={productClassName} onClick={handleProductClick} data-testid="navlog-product">
			<div className="navlog-product-card__picture">
				{resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL ? (
					<BackgroundProgressiveImg
						sizes={IMG_SIZES_DESKTOP}
						cloudinaryOptions={CLOUDINARY_OPTIONS}
						src={photo.url}
						alt={resort}
						thumbnail={photo.thumbnail}
						productUri={uri}
					/>
				) : (
					<ProgressiveImg
						className="progressive-img"
						sizes={IMG_SIZES_DESKTOP}
						src={photo.url}
						alt={resort}
						thumbnail={photo.thumbnail}
						productUri={uri}
					/>
				)}

				<div className="navlog-product-card__topbar">
					<ProductTopBar
						badges={
							quotationURL && [
								{
									bgColor: "white",
									borderColor: "white",
									txtColor: "black",
									label: <FormattedMessage id="listing.ongoing.quote" />,
								},
							]
						}
						productExpirationHours={expiration?.hour}
						productExpirationDays={expiration?.day}
					/>
				</div>
			</div>
			<div className="navlog-product-card__footer">
				<Typography
					variant={TYPOGRAPHY_VARIANTS.LARGE}
					variantMD={TYPOGRAPHY_VARIANTS.LARGE}
					isBold
				>
					<ProductDestinationTitleContainer
						region={region}
						resort={resort}
						country={country}
					/>
				</Typography>
				<Typography
					variant={TYPOGRAPHY_VARIANTS.SMALL}
					variantMD={TYPOGRAPHY_VARIANTS.SMALL}
					isBold
				>
					<ProductNameWithCategory
						categoryType={categoryType}
						category={category}
						name={name}
					/>
				</Typography>
			</div>
		</div>
	);
};

NavLogProductCard.propTypes = {
	categoryType: PropTypes.string,
	category: PropTypes.number,
	country: PropTypes.string,
	region: PropTypes.string,
	resort: PropTypes.string,
	expiration: PropTypes.shape({
		day: PropTypes.number,
		hour: PropTypes.number,
	}),
	name: PropTypes.string,
	photo: PropTypes.shape({
		thumbnail: PropTypes.string,
		url: PropTypes.string,
	}),
	uri: PropTypes.string,
	resolution: resolutionPropType,
	quotationURL: PropTypes.string,
};

export default memo(NavLogProductCard);
